(() => {
  if (customElements.get('design-collection')) return;

  class DesignCollection extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.uiEvents = new AbortController();
      this.selectedProductId = '';
      this.productCards = this.dataset.catalogMode === 'products';
      this.activeFilters = this.emptyFilters();
      this.sortBy = 'recommended';
      this.products = new Map();
      this.productPreviews = new Map();

      this.querySelectorAll('[data-product-record]').forEach((record) => {
        const variants = new Map();
        record.querySelectorAll('[data-variant-record]').forEach((variant) => {
          variants.set(variant.dataset.designKey, {
            key: variant.dataset.designKey,
            deviceTitle: variant.dataset.deviceTitle,
            createdAt: Number(variant.dataset.createdAt) || 0,
            title: variant.dataset.title,
            skinType: variant.dataset.skinType,
            categories: this.parseCategories(variant.dataset.categories),
            sellIndex: this.parseSellIndex(variant.dataset.sellIndex),
            variantId: variant.dataset.variantId,
            available: variant.dataset.available === 'true',
            price: variant.dataset.price,
            url: variant.dataset.url,
            image: variant.dataset.image,
            imageAlt: variant.dataset.imageAlt,
            badge: variant.dataset.badge,
            badgeColor: variant.dataset.badgeColor,
            badgeTextColor: variant.dataset.badgeTextColor
          });
        });
        if (this.productCards) {
          variants.forEach((variant, deviceHandle) => {
            if (!this.products.has(deviceHandle)) this.products.set(deviceHandle, {
              id: deviceHandle, deviceHandle, deviceTitle: variant.deviceTitle, variants: new Map(),
            });
            this.products.get(deviceHandle).variants.set(record.dataset.productId, variant);
          });
          const preview = [...variants.values()].find(variant => variant.variantId === record.dataset.previewVariantId);
          if (preview) this.productPreviews.set(record.dataset.productId, {
            ...preview,
            price: record.dataset.previewPrice,
            image: record.dataset.previewImage || preview.image,
            categories: [preview.categories[0], ...(record.dataset.productAvailable === 'true' ? ['available'] : [])],
          });
        } else this.products.set(record.dataset.productId, {
          id: record.dataset.productId,
          deviceTitle: record.dataset.deviceTitle,
          deviceHandle: record.dataset.deviceHandle,
          variants
        });
      });
      this.storageKey = `marmelade:collection-device:${this.dataset.collectionHandle}`;
      this.defaultProductId = this.products.has(this.dataset.defaultProductId)
        ? this.dataset.defaultProductId
        : this.products.keys().next().value;
      const onlyProduct = this.products.size === 1 ? this.products.values().next().value : null;
      this.selectedProductId = onlyProduct?.id || this.restoreSelectedProductId();
      this.allDesignsProduct = this.buildAllDesignsProduct();

      this.grid = this.querySelector('[data-design-grid]');
      this.cards = [...this.querySelectorAll('[data-design-card]')];
      this.defaultOrder = this.cards.map((card) => card.dataset.designKey);
      this.deviceDialog = this.querySelector('[data-device-dialog]');
      this.filterDialog = this.querySelector('[data-filter-dialog]');
      this.sortRoot = this.querySelector('[data-sort-root]');
      this.sortMenu = this.querySelector('[data-sort-menu]');
      this.deviceName = this.querySelector('[data-device-name]');
      if (this.deviceName) this.deviceName.dataset.defaultLabel = this.deviceName.textContent || '';
      this.bindEvents();
      this.bindSheets();
      this.updateCards();
    }

    emptyFilters() {
      return this.productCards ? { product: '', availability: '' } : { color: '', material: '' };
    }

    bindSheets() {
      this.sheets = new Map([this.deviceDialog, this.filterDialog].map(dialog => [dialog, new window.MarmeladeFlyout(dialog, {
        panel: dialog.querySelector(':scope > section'),
        handle: dialog.querySelector('[data-device-drag-handle], [data-filter-drag-handle]'),
        dismiss: dialog.querySelector('[data-device-drag-dismiss], [data-filter-dismiss]'),
        backdrop: dialog.querySelector('[data-dialog-backdrop]'),
        requestClose: options => this.closeDialogs(options),
        initialFocus: () => dialog.querySelector('[data-device-option][aria-pressed="true"]'),
      })]));
    }

    parseCategories(value) {
      try {
        const parsed = JSON.parse(value || '[]');
        return Array.isArray(parsed) ? parsed.map((category) => this.normalizeFilterValue(category)) : [];
      } catch (error) {
        return [];
      }
    }

    normalizeFilterValue(value) {
      return String(value || '').trim().toLocaleLowerCase();
    }

    parseSellIndex(value) {
      const normalizedValue = String(value ?? '').trim();
      if (normalizedValue === '') return null;
      const sellIndex = Number(normalizedValue);
      return Number.isFinite(sellIndex) ? sellIndex : null;
    }

    compareSellIndexes(first, second) {
      const firstHasSellIndex = Number.isFinite(first);
      const secondHasSellIndex = Number.isFinite(second);
      if (firstHasSellIndex !== secondHasSellIndex) return firstHasSellIndex ? -1 : 1;
      if (!firstHasSellIndex) return 0;
      return second - first;
    }

    buildAllDesignsProduct() {
      if (this.productCards) return { id: '', deviceTitle: '', deviceHandle: '', variants: this.productPreviews };
      const variants = new Map();
      const defaultProduct = this.products.get(this.defaultProductId);

      defaultProduct?.variants.forEach((variant, designKey) => {
        variants.set(designKey, variant);
      });

      this.products.forEach((product) => {
        if (product.id === this.defaultProductId) return;
        product.variants.forEach((variant, designKey) => {
          if (!variants.has(designKey)) variants.set(designKey, variant);
        });
      });

      return {
        id: '',
        deviceTitle: '',
        deviceHandle: '',
        variants
      };
    }

    bindEvents() {
      this.querySelector('[data-device-trigger]')?.addEventListener('click', () => this.openDialog(this.deviceDialog), { signal: this.uiEvents.signal });
      this.querySelector('[data-filter-trigger]')?.addEventListener('click', () => this.openDialog(this.filterDialog), { signal: this.uiEvents.signal });
      this.querySelector('[data-apply-filters]')?.addEventListener('click', () => this.closeDialogs(), { signal: this.uiEvents.signal });
      this.querySelector('[data-reset-filters]')?.addEventListener('click', () => {
        this.activeFilters = this.emptyFilters();
        this.syncFilterButtons();
        this.updateCards();
      }, { signal: this.uiEvents.signal });
      this.querySelectorAll('[data-filter-option]').forEach((button) => button.addEventListener('click', () => {
        this.activeFilters[button.dataset.filterGroup] = this.normalizeFilterValue(button.dataset.filterValue);
        this.syncFilterButtons();
        this.updateCards();
      }, { signal: this.uiEvents.signal }));
      this.querySelectorAll('[data-device-option]').forEach((button) => button.addEventListener('click', () => {
        const previousProduct = this.selectedProductId ? this.products.get(this.selectedProductId) : null;
        const selectedProduct = this.products.get(button.dataset.deviceOption);
        if (!selectedProduct || selectedProduct.id === previousProduct?.id) {
          this.closeDialogs();
          return;
        }
        this.selectedProductId = button.dataset.deviceOption;
        this.persistSelectedDevice(selectedProduct);
        this.updateCards();
        this.closeDialogs();
        window.MarmeladeAnalytics.capture('device selected', {
          surface: 'collection',
          collection_handle: this.dataset.collectionHandle,
          device_product_id: this.productCards ? null : selectedProduct.id,
          device_handle: selectedProduct.deviceHandle,
          device_title: selectedProduct.deviceTitle,
          previous_device_handle: previousProduct?.deviceHandle || ''
        });
      }, { signal: this.uiEvents.signal }));
      this.querySelector('[data-sort-trigger]')?.addEventListener('click', () => this.toggleSort(), { signal: this.uiEvents.signal });
      this.querySelectorAll('[data-sort-option]').forEach((button) => button.addEventListener('click', () => {
        this.sortBy = button.dataset.sortOption;
        this.querySelector('[data-sort-label]').textContent = button.dataset.label;
        this.querySelectorAll('[data-sort-option]').forEach((option) => option.setAttribute('aria-selected', String(option === button)));
        this.closeSort();
        this.updateCards();
      }, { signal: this.uiEvents.signal }));
      document.addEventListener('pointerdown', (event) => {
        if (!this.sortRoot?.contains(event.target)) this.closeSort();
      }, { signal: this.uiEvents.signal });
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        this.closeSort();
      }, { signal: this.uiEvents.signal });
    }

    disconnectedCallback() {
      this.uiEvents?.abort();
      this.sheets?.forEach(sheet => sheet.destroy());
      delete this.dataset.initialized;
    }

    restoreSelectedProductId() {
      try {
        const storedDeviceHandle = window.localStorage.getItem(this.storageKey);
        if (!storedDeviceHandle) return '';
        const storedProduct = [...this.products.values()].find((product) => product.deviceHandle === storedDeviceHandle);
        if (storedProduct) return storedProduct.id;
        window.localStorage.removeItem(this.storageKey);
      } catch (_error) {
        return '';
      }
      return '';
    }

    persistSelectedDevice(product) {
      try {
        window.localStorage.setItem(this.storageKey, product.deviceHandle);
      } catch (_error) {
        // Storage can be unavailable in restrictive privacy modes; selection still works for the current page.
      }
    }

    openDialog(dialog) {
      this.closeSort();
      const trigger = this.querySelector(dialog === this.deviceDialog ? '[data-device-trigger]' : '[data-filter-trigger]');
      this.sheets.get(dialog).open(trigger);
    }

    closeDialogs(options) {
      return Promise.all([...this.sheets.values()].map(sheet => sheet.close(options)));
    }

    toggleSort() {
      const shouldOpen = this.sortMenu?.hidden;
      if (!this.sortMenu) return;
      this.sortMenu.hidden = !shouldOpen;
      this.sortRoot.classList.toggle('is-open', shouldOpen);
      this.querySelector('[data-sort-trigger]')?.setAttribute('aria-expanded', String(shouldOpen));
    }

    closeSort() {
      if (!this.sortMenu) return;
      this.sortMenu.hidden = true;
      this.sortRoot?.classList.remove('is-open');
      this.querySelector('[data-sort-trigger]')?.setAttribute('aria-expanded', 'false');
    }

    syncFilterButtons() {
      this.querySelectorAll('[data-filter-option]').forEach((button) => {
        const isActive = this.activeFilters[button.dataset.filterGroup] === this.normalizeFilterValue(button.dataset.filterValue);
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    }

    updateCards() {
      const activeProduct = this.selectedProductId
        ? this.products.get(this.selectedProductId)
        : this.allDesignsProduct;
      if (!activeProduct) return;
      const selectedFilters = Object.values(this.activeFilters).filter(Boolean);
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const variant = activeProduct.variants.get(card.dataset.designKey);
        if (!variant) {
          card.hidden = true;
          return;
        }
        const isVisible = selectedFilters.every((filter) => variant.categories.includes(filter));
        card.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
        card.dataset.sellIndex = variant.sellIndex == null ? '' : String(variant.sellIndex);
        card.dataset.variantId = variant.variantId;
        card.dataset.categories = JSON.stringify(variant.categories);
        const unavailable = Boolean(this.selectedProductId) && !variant.available;
        card.classList.toggle('is-unavailable', unavailable);
        const availability = card.querySelector('[data-card-availability]');
        if (availability) availability.hidden = !unavailable;
        card.href = variant.url + '&device=' + (this.selectedProductId
          ? encodeURIComponent(activeProduct.deviceHandle)
          : 'unselected');
        card.querySelector('[data-card-title]').textContent = variant.title;
        card.querySelector('[data-card-type]').textContent = variant.skinType;
        card.querySelector('[data-card-price]').textContent = variant.price;
        const image = card.querySelector('[data-card-image]');
        if (image && variant.image) {
          image.src = variant.image;
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.alt = variant.imageAlt;
        }
        const badge = card.querySelector('[data-card-badge]');
        if (badge) {
          badge.textContent = variant.badge;
          badge.hidden = !variant.badge;
          if (variant.badgeColor) {
            badge.style.setProperty('--design-badge-background', variant.badgeColor);
            badge.style.setProperty('--design-badge-foreground', variant.badgeTextColor || '#ffffff');
          } else {
            badge.style.removeProperty('--design-badge-background');
            badge.style.removeProperty('--design-badge-foreground');
          }
        }
      });

      this.sortCards(activeProduct);
      this.updateDeviceState(activeProduct);
      this.updateFilterState();
      this.querySelector('[data-empty-state]').hidden = visibleCount !== 0;
    }

    sortCards(activeProduct) {
      const orderIndex = new Map(this.defaultOrder.map((key, index) => [key, index]));
      const cards = [...this.cards];
      cards.sort((a, b) => {
        const aVariant = activeProduct.variants.get(a.dataset.designKey);
        const bVariant = activeProduct.variants.get(b.dataset.designKey);
        if (!aVariant || !bVariant) return Number(!aVariant) - Number(!bVariant);
        if (this.sortBy === 'name') return aVariant.title.localeCompare(bVariant.title, document.documentElement.lang);
        if (this.sortBy === 'newest') return this.productCards
          ? bVariant.createdAt - aVariant.createdAt
          : (orderIndex.get(b.dataset.designKey) || 0) - (orderIndex.get(a.dataset.designKey) || 0);
        return this.compareSellIndexes(aVariant.sellIndex, bVariant.sellIndex);
      });
      cards.forEach((card) => this.grid.append(card));
    }

    updateDeviceState(activeProduct) {
      const hasSelection = Boolean(this.selectedProductId);
      const trigger = this.querySelector('[data-device-trigger]');
      trigger?.classList.toggle('is-selected', hasSelection);
      this.querySelector('[data-device-prefix]').hidden = !hasSelection;
      if (this.deviceName) this.deviceName.textContent = hasSelection ? activeProduct.deviceTitle : this.deviceName.dataset.defaultLabel;
      this.querySelectorAll('[data-device-option]').forEach((button) => {
        const selected = button.dataset.deviceOption === this.selectedProductId;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });
      const count = this.cards.filter((card) => !card.hidden).length;
      const template = count === 1 && this.productCards
        ? (hasSelection ? this.dataset.countDeviceOneLabel : this.dataset.countOneLabel)
        : (hasSelection ? this.dataset.countDeviceLabel : this.dataset.countLabel);
      this.querySelector('[data-design-count]').textContent = template.replace('__count__', String(count)).replace('__device__', activeProduct.deviceTitle);
    }

    updateFilterState() {
      const activeCount = Object.values(this.activeFilters).filter(Boolean).length;
      const label = activeCount
        ? this.dataset.filterActiveLabel.replace('__count__', String(activeCount))
        : this.dataset.filterLabel;
      this.querySelector('[data-filter-trigger-label]').textContent = label;
    }
  }

  customElements.define('design-collection', DesignCollection);
})();
